import { memo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useGetInputBindingSlot } from 'app/modules/wallforming/components/input-binding/hooks/useGetInputBindingSlot';
import { PointerGestureEditor } from 'app/modules/wallforming/components/input-binding/PointerGestureEditor';
import { inputActions } from 'app/modules/wallforming/components/input-binding/registry/inputActionsRegistry';
import { SlotActionIcon } from 'app/modules/wallforming/components/input-binding/SlotActionIcon';
import { usePlacementStore } from 'app/modules/wallforming/components/state-management/placement';

import type { InputActionId } from 'app/modules/wallforming/components/input-binding/types/inputActionId';

const POINTER_ACTIONS_COLUMN = '3.5rem';
const POINTER_TABLE_COLUMNS = `minmax(10rem, 1fr) minmax(200px, 240px) minmax(90px, 110px) ${POINTER_ACTIONS_COLUMN}`;
const POINTER_TABLE_COLUMNS_WITH_CLICK = `minmax(10rem, 1fr) minmax(200px, 240px) minmax(90px, 110px) minmax(68px, 74px) ${POINTER_ACTIONS_COLUMN}`;

export const PointerBindingRow = memo(function PointerBindingRow({
    actionId,
    showClickType
}: {
    actionId: InputActionId;
    showClickType: boolean;
}) {
    const { t } = useTranslation();
    const { chord, isModified, isReadOnly } = useGetInputBindingSlot(actionId, 0);
    const { assignInputBinding, resetInputBindingSlot } = usePlacementStore.getState();
    const action = inputActions[actionId];
    const [hasPendingValue, setHasPendingValue] = useState(false);
    const [editorResetKey, setEditorResetKey] = useState(0);

    const clearBinding = () => {
        assignInputBinding(actionId, 0, null);
        setHasPendingValue(false);
        setEditorResetKey((key) => key + 1);
    };

    const resetBinding = () => {
        resetInputBindingSlot(actionId, 0);
        setHasPendingValue(false);
        setEditorResetKey((key) => key + 1);
    };

    return (
        <div
            className="grid items-center gap-x-2 rounded-md px-4 py-1 hover:bg-background-gray-ultra-light"
            style={{ gridTemplateColumns: showClickType ? POINTER_TABLE_COLUMNS_WITH_CLICK : POINTER_TABLE_COLUMNS }}
        >
            <div className="flex min-w-0 items-center">
                <span className="whitespace-normal wrap-break-word text-xs leading-tight">{t(action.labelKey)}</span>
            </div>
            <PointerGestureEditor
                key={`${chord ?? 'empty'}-${editorResetKey}`}
                actionId={actionId}
                showClickType={showClickType}
                slot={0}
                onPendingChange={setHasPendingValue}
            />
            <div className="flex shrink-0 items-center justify-end">
                <SlotActionIcon
                    disabled={isReadOnly || (!chord && !hasPendingValue)}
                    icon="close"
                    labelKey="labelKeyBindingClearSlot"
                    onClick={clearBinding}
                />
                <SlotActionIcon
                    disabled={isReadOnly || !isModified}
                    icon="restart_alt"
                    labelKey="labelKeyBindingResetSlot"
                    onClick={resetBinding}
                />
            </div>
        </div>
    );
});
